/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { EmptyState } from '@clientbase/clientbase-library'
import { Box } from '@mui/system'
import { customerChargesList, customerRecentChargesList } from 'api/requests'
import { AuthContext } from 'context/AuthContext'
import {
  useTableColumns,
  ColumnSelectorTrigger,
  ColumnSelectorPopover
} from 'features/table-columns'
import { catalog } from 'features/table-columns/catalog/cobrancas-cobrancas'
import type { PaidPayment, RecordMerchant } from 'features/table-columns/cells'
import { SelectRowTable } from 'hooks/use-select-row-table'
import { aggregatedScope, registerRecordMerchant } from 'lib/merchant-group'
import { NfseScheduled } from 'models/NFe'
import { roles } from 'models/User'
import { ParsedUrlQuery } from 'querystring'
import { ToggleModalsParams } from 'templates/v2/Charge/Charge'

import { Info } from 'components/InfoList/InfoList'
import { EnhancedTable, SortableColumn } from 'components/Table'
import { MultiSortConfig } from 'components/Table/SortableTableHead'
import { HeadCell, Order } from 'components/Table/Table.interface'
import { TableSkeleton } from 'components/Table/TableSkeleton'

import browserQueryToQueryString from 'utils/query-string'

import { CustomerCharges, statisticsMapper } from './'

export interface DataCustomerCharges {
  error: boolean
  records: Record[]
  meta: Meta
  statistics: StatisticsMapperParams
}

export type BreakdownByPeriod = {
  total: number
  not_due: number
  due_up_to_7_days: number
  due_more_than_7_days: number
  churn?: number
}

export type StatisticsMapperParams = {
  total_billed: number
  total_count: number
  total_paid: number
  total_received: number
  total_to_receive: number
  total_not_due: number
  total_due_up_to_7_days: number
  total_due_more_than_7_days: number
  total_churn: number
  total_due: number
  total_leads: number
  // Novos campos hierárquicos (podem não existir ainda na API)
  leads?: BreakdownByPeriod
  customers?: BreakdownByPeriod
  // Breakdown do recebido
  total_received_original?: number
  total_received_fine_and_fee?: number
  total_received_discount?: number
  total_received_bonification?: number
}

export type NfseStatus = 'scheduled' | 'issued' | 'not_scheduled'

export interface Record {
  customer: Customer
  due_date: string
  amount_paid: string
  date_paid: string | null
  amount_billed: string
  status: string
  uuid: string
  viewed: boolean
  /**
   * SC-10890: total de aberturas e última abertura. Opcionais de propósito —
   * front e back sobem em momentos diferentes, e `undefined` tem que cair no
   * comportamento booleano antigo (nunca "abriu 0 vezes").
   */
  open_count?: number
  last_opened_at?: string | null
  recurrence_uuid: string
  nfse_status: NfseStatus
  nfse: Nfse
  nfse_scheduled?: NfseScheduled
  nfse_policy: string
  /**
   * Pagamentos da cobrança. A listagem `/v3/billings` já os devolve — o encoder
   * serializa `payments` com o `paymentable` junto e descarta só os status
   * mortos. É de onde sai o meio de pagamento REALIZADO (o de status `paid`);
   * `payment_type` na raiz é outra coisa, a lista do que a cobrança aceita.
   */
  payments?: PaidPayment[]
  // SC-10999: só nas respostas agregadas (visão multi-empresas)
  merchant?: RecordMerchant | null
}

export interface Nfse {
  status: NfseStatus
  uuid: string
}

export interface Customer {
  name: string
  nickname: string
  uuid: string
  category?: string
}

interface Meta {
  current_page: number
  next_page: number
  prev_page: any
  total_pages: number
  total_count: number
}

// tableHeader and sortableColumns are now derived dynamically from the column
// catalog via useTableColumns() inside the component.

type ChargeTableProps = {
  toggleModals: (params: ToggleModalsParams) => void
  baseFilters: { [key in string]: string } | ParsedUrlQuery
  setInfoList?: React.Dispatch<
    React.SetStateAction<
      | {
          standardCards: Info[]
          receivedCards: Info[]
          toReceiveCards: Info[]
        }
      | undefined
    >
  >
  setBaseFilters: (params: {
    [key in string]: string | number | object
  }) => void
  updateTable?: boolean
  recentCharges?: boolean
  setHasRecentCharges?: React.Dispatch<React.SetStateAction<boolean>>
  customerPage?: boolean
  setRawStatistics?: React.Dispatch<React.SetStateAction<any>>
  isScheduledTab?: boolean
  isLoadingTabChange?: boolean
  setIsLoadingTabChange?: React.Dispatch<React.SetStateAction<boolean>>
  hideActions?: boolean
  select?: {
    useSelect: boolean
    selected: string[]
    setSelected: React.Dispatch<React.SetStateAction<SelectRowTable>>
  }
  /** Registra os records crus de cada página para o mapa uuid→status (elegibilidade do batch). */
  onRegisterRecords?: (records: Record[]) => void
  /** Reporta o total de cobranças do filtro atual (para o "Selecionar todas as N"). */
  onTotalCountChange?: (total: number) => void
}

const ChargeTable = ({
  baseFilters,
  setBaseFilters,
  toggleModals,
  setInfoList,
  updateTable,
  recentCharges,
  setHasRecentCharges,
  customerPage,
  setRawStatistics,
  isScheduledTab,
  isLoadingTabChange,
  setIsLoadingTabChange,
  hideActions,
  select,
  onRegisterRecords,
  onTotalCountChange
}: ChargeTableProps) => {
  const { user } = useContext(AuthContext)
  const AUTHORIZATION = roles[user?.role || 'guest']
  const [loading, setLoading] = useState(true)

  // Column selector state
  const [selectorAnchorEl, setSelectorAnchorEl] = useState<HTMLElement | null>(
    null
  )
  const selectorOpen = Boolean(selectorAnchorEl)

  // Column catalog — filter out customer column when on customer page,
  // and date_paid / amount_paid when on scheduled tab (mirrors old behavior)
  const effectiveCatalog = useMemo(() => {
    return catalog.filter((col) => {
      if (customerPage && col.id === 'customer') return false
      if (
        isScheduledTab &&
        (col.id === 'date_paid' || col.id === 'amount_paid')
      )
        return false
      return true
    })
  }, [customerPage, isScheduledTab])

  const {
    visibleColumns,
    allColumns,
    pref,
    applyPref,
    reset,
    isReady,
    tableProps
  } = useTableColumns({
    tableId: 'cobrancas-cobrancas',
    catalog: effectiveCatalog
  })

  // Build table header from visible columns
  const dynamicTableHeader = useMemo((): HeadCell[] => {
    return visibleColumns.map((col) => ({
      id: col.id,
      label:
        col.id === 'actions' ? (
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <ColumnSelectorTrigger
              onClick={(e) => setSelectorAnchorEl(e.currentTarget)}
              active={selectorOpen}
              hiddenOnMobile
            />
          </Box>
        ) : col.header ? (
          col.header()
        ) : (
          col.label
        )
    }))
  }, [visibleColumns, selectorOpen])

  // Build sortable columns from visible columns
  const dynamicSortableColumns = useMemo((): SortableColumn[] => {
    return visibleColumns.map((col) =>
      col.sortable
        ? { id: col.id, sortable: true, sortField: col.sortable.sortField }
        : { id: col.id, sortable: false }
    )
  }, [visibleColumns])

  const [customerCharges, setCustomerCharges] = useState({} as CustomerCharges)
  // Espelha `loading` de forma síncrona. O efeito de `visibleColumns` roda no
  // MESMO commit que o efeito de filtros (trocar de aba muda a query e o
  // catálogo de colunas de uma vez), e lá o `loading` do closure ainda é o do
  // render anterior — sem o ref, a troca de aba dispara dois fetches.
  const loadingRef = useRef(false)
  // Duas chamadas podem estar no ar ao mesmo tempo (troca rápida de aba, clique
  // em card logo após entrar). Sem isso a que RESPONDE por último vence, mesmo
  // sendo a mais velha — os indicadores carregam certo e depois "voltam".
  const requestIdRef = useRef(0)
  const handleGetCustomerCharges = async () => {
    const requestId = ++requestIdRef.current
    const isStale = () => requestId !== requestIdRef.current

    const { detailed, received_detailed, ...filtersWithoutDetailed } =
      baseFilters
    try {
      loadingRef.current = true
      setLoading(true)
      setCustomerCharges({} as CustomerCharges)
      setInfoList && setInfoList(undefined)
      let response

      if (recentCharges) {
        response = await customerRecentChargesList<DataCustomerCharges>({
          uuid: baseFilters.id as string
        })

        !!setHasRecentCharges &&
          setHasRecentCharges(response?.data?.records?.length > 0)
      } else {
        response = await customerChargesList<DataCustomerCharges>({
          filter_by: browserQueryToQueryString(
            filtersWithoutDetailed as { [key in string]: string }
          ),
          // SC-10999: na visão multi-empresas a lista vem agregada do grupo
          scope: aggregatedScope()
        })
      }

      if (isStale()) return

      const data: DataCustomerCharges = response.data as DataCustomerCharges

      // Se for customerPage, passar as estatísticas brutas para o componente pai
      if (customerPage && setRawStatistics) {
        setRawStatistics(data?.statistics)
      } else if (setInfoList) {
        // Caso contrário, usar o mapper padrão (statistics pode vir null se sem permissão)
        setInfoList(
          data?.statistics ? statisticsMapper(data.statistics) : undefined
        )
      }

      // SC-10999: guarda a empresa de cada registro (e dos registros ligados
      // que as ações da linha abrem: recorrência e nota) para o detalhe agir
      // nela.
      data?.records?.forEach((record) => {
        const merchantUuid = record?.merchant?.uuid
        registerRecordMerchant(record?.uuid, merchantUuid)
        registerRecordMerchant(record?.recurrence_uuid, merchantUuid)
        registerRecordMerchant(record?.nfse?.uuid, merchantUuid)
        registerRecordMerchant(record?.nfse_scheduled?.uuid, merchantUuid)
      })

      const ctx = {
        AUTHORIZATION,
        toggleModals,
        notViewActions: hideActions,
        baseFilters
      }

      const rows = data?.records?.map((record) => {
        const rowCells = Object.fromEntries(
          visibleColumns.map((col) => [col.id, col.cell(record, ctx)])
        )
        return {
          uuid: record?.uuid,
          ...rowCells
        }
      })

      setCustomerCharges({
        totalCount: data?.meta?.total_count,
        rows
      } as CustomerCharges)

      onRegisterRecords?.(data?.records ?? [])
      onTotalCountChange?.(data?.meta?.total_count ?? 0)
    } finally {
      // Resposta obsoleta não desliga o loading: a chamada mais nova ainda está
      // em voo e o skeleton tem que continuar até ela chegar.
      if (!isStale()) {
        loadingRef.current = false
        setLoading(false)
        setIsLoadingTabChange && setIsLoadingTabChange(false)
      }
    }
  }

  const handleChangePage = (page: number) => {
    setBaseFilters({ query: { ...baseFilters, page } })
  }

  const handleChangeRowsPerPage = (per: string | number) => {
    setBaseFilters({ query: { ...baseFilters, per, page: 0 } })
  }

  /**
   * Handle server-side sorting (single column - backwards compatible)
   * When a column header is clicked, update the URL with the new sort parameter
   * and reset to page 0
   */
  const handleSort = (field: string, direction: Order) => {
    if (!direction) {
      // Remove sort if direction is undefined (third click)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { sort, ...filtersWithoutSort } = baseFilters
      setBaseFilters({ query: { ...filtersWithoutSort, page: 0 } })
    } else {
      // Add or update sort
      setBaseFilters({
        query: {
          ...baseFilters,
          sort: `${field}:${direction}`,
          page: 0
        }
      })
    }
  }

  /**
   * Handle multi-column sorting
   * Updates the URL with comma-separated sort parameters
   * Format: field1:direction1,field2:direction2
   */
  const handleMultiSort = (sorts: MultiSortConfig) => {
    if (sorts.length === 0) {
      // Remove sort if no sorts
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { sort, ...filtersWithoutSort } = baseFilters
      setBaseFilters({ query: { ...filtersWithoutSort, page: 0 } })
    } else {
      // Build comma-separated sort string
      const sortString = sorts.map((s) => `${s.field}:${s.direction}`).join(',')
      setBaseFilters({
        query: {
          ...baseFilters,
          sort: sortString,
          page: 0
        }
      })
    }
  }

  /**
   * Parse current sort configuration from URL
   * Supports both single and multi-column sorts
   * Format: field:direction or field1:direction1,field2:direction2
   */
  const sortConfig: MultiSortConfig = useMemo(() => {
    const sortParam = baseFilters.sort as string | undefined
    if (!sortParam) return []

    // Split by comma for multi-column sorting
    const sortParts = sortParam.split(',')
    const sorts: MultiSortConfig = []

    for (const part of sortParts) {
      const [field, direction] = part.split(':')
      if (field && direction) {
        sorts.push({
          field,
          direction: direction as Order
        })
      }
    }

    return sorts
  }, [baseFilters.sort])

  const prevFiltersRef = useRef({})
  const prevUpdateTableRef = useRef(updateTable)

  useEffect(
    () => {
      if (!isReady) return

      const {
        detailed: currentDetailed,
        received_detailed: currentReceivedDetailed,
        ...currentFiltersWithoutDetailed
      } = baseFilters
      const {
        detailed: prevDetailed,
        received_detailed: prevReceivedDetailed,
        ...prevFiltersWithoutDetailed
      } = prevFiltersRef.current as any

      const currentStr = JSON.stringify(currentFiltersWithoutDetailed)
      const prevStr = JSON.stringify(prevFiltersWithoutDetailed)

      const filtersChanged = currentStr !== prevStr

      // `updateTable` é um booleano alternado pelo contexto (updateTable() faz
      // setReloadTable(p => !p)), não um evento. Testar o VALOR fazia o fetch
      // repetir a cada mudança de identidade de `baseFilters` — que é o `query`
      // do router — durante todo o período em que ele estivesse `true`.
      const updateTableChanged = prevUpdateTableRef.current !== updateTable

      prevFiltersRef.current = baseFilters
      prevUpdateTableRef.current = updateTable

      if (filtersChanged || updateTableChanged) {
        handleGetCustomerCharges()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    recentCharges ? [updateTable, isReady] : [baseFilters, updateTable, isReady]
  )

  // Re-map rows when visible columns change (user toggled/reordered)
  useEffect(() => {
    if (!isReady || loadingRef.current) return
    handleGetCustomerCharges()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns])

  if (loading || isLoadingTabChange || !isReady) {
    return (
      <TableSkeleton
        columns={visibleColumns.length || 7}
        hidePagination={recentCharges}
      />
    )
  }

  return (
    <>
      {!customerCharges?.rows?.length ? (
        <Box display="flex" justifyContent="center">
          <Box maxWidth={{ xs: '100%', md: '60%' }} width="100%" mt="18vh">
            <EmptyState
              title="Nenhuma cobrança encontrada"
              message="Tente ajustar os filtros ou cadastre uma nova cobrança para começar a visualizar."
            />
          </Box>
        </Box>
      ) : (
        <EnhancedTable
          {...tableProps}
          header={dynamicTableHeader}
          handleChangePage={(_, page) => handleChangePage(page)}
          handleChangeRowsPerPage={(e) =>
            handleChangeRowsPerPage(e.target.value)
          }
          page={Number(baseFilters?.page) || 0}
          rowsPerPage={Number(baseFilters?.per) || 25}
          rowAction={(uuid) =>
            toggleModals({ modal: 'isOpenModalChargeDetail', uuid })
          }
          hiddePagination={recentCharges}
          hoverInRow
          select={
            select
              ? {
                  useSelect: select.useSelect,
                  selected: select.selected,
                  setSelected: select.setSelected
                }
              : { useSelect: false }
          }
          // Server-side sorting props
          sortConfig={sortConfig}
          onSort={handleSort}
          onMultiSort={handleMultiSort}
          multiSort={true}
          sortableColumns={dynamicSortableColumns}
          disableSort={recentCharges}
          // Em cobranças o botão Ordenar vive na barra de filtros, junto de
          // "Selecionar" e "Filtros"; o card não desenha o dele.
          mobileCards={{ ...tableProps.mobileCards, sortInToolbar: true }}
          {...customerCharges}
        />
      )}

      <ColumnSelectorPopover<Record>
        anchorEl={selectorAnchorEl}
        open={selectorOpen}
        onClose={() => setSelectorAnchorEl(null)}
        allColumns={allColumns}
        pref={pref}
        onApply={applyPref}
        onReset={reset}
      />
    </>
  )
}

export default ChargeTable
